import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import VisaFormField from './VisaFormField';
import CountrySelect from './CountrySelect';
import { useCountries } from '../hooks/useCountries';
import {
  parseVisaFormResponse,
  getVisaFormRequestParams,
  getRouteFormTemplateId,
  flattenFormSections,
  buildFormFieldLabels,
} from '../utils/visaFormHelpers';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const freshRequestConfig = {
  headers: {
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
  },
};

const formatFeeAmount = (charge, field) => {
  if (!charge) return '-';
  const displayKey = `${field}Display`;
  if (charge[displayKey]) return charge[displayKey];
  const value = charge[field];
  if (value == null || value === '') return '-';
  const currency = charge.vault_currency || charge.currency;
  return currency ? `${currency} ${value}` : String(value);
};

const FeeSummary = ({ serviceFee }) => {
  if (!serviceFee) return null;

  const feeCurrency = serviceFee.fee_currency;
  const vaultCurrency = serviceFee.vault_currency || serviceFee.currency;

  return (
    <div className="rounded-xl bg-gray-50 px-4 py-3 text-sm text-gray-700 space-y-1">
      {serviceFee.price ? (
        <p>
          Visa price ({feeCurrency || 'API'}):{' '}
          <strong>
            {serviceFee.originalPriceDisplay || formatFeeAmount(serviceFee, 'price')}
          </strong>
        </p>
      ) : null}
      <p>
        Service fee ({feeCurrency || 'API'}):{' '}
        <strong>
          {serviceFee.originalServiceFeeDisplay || formatFeeAmount(serviceFee, 'serviceFee')}
        </strong>
      </p>
      <p>
        Total to deduct from vault
        {vaultCurrency ? ` (${vaultCurrency})` : ''}:{' '}
        <strong>{formatFeeAmount(serviceFee, 'totalAmount')}</strong>
      </p>
      {serviceFee.converted ? (
        <p className="text-xs text-gray-500 pt-1">
          Converted {serviceFee.originalTotalDisplay}
          {feeCurrency && vaultCurrency ? ` (${feeCurrency} → ${vaultCurrency})` : ''}
          {serviceFee.conversion?.rate ? ` @ ${serviceFee.conversion.rate}` : ''}
        </p>
      ) : feeCurrency ? (
        <p className="text-xs text-gray-500 pt-1">
          Fee currency matches from-country ({feeCurrency}); no conversion needed.
        </p>
      ) : null}
    </div>
  );
};

const ApplyVisaSection = ({ onSubmitted }) => {
  const [fromCountry, setFromCountry] = useState('');
  const [toCountry, setToCountry] = useState('');
  const [routes, setRoutes] = useState([]);
  const [routeMeta, setRouteMeta] = useState(null);
  const [selectedVisa, setSelectedVisa] = useState(null);
  const [formDefinition, setFormDefinition] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [serviceFee, setServiceFee] = useState(null);
  const [requiredDocuments, setRequiredDocuments] = useState([]);
  const [documentGroupName, setDocumentGroupName] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { countries, loading: countriesLoading } = useCountries();

  const parsedForm = useMemo(() => parseVisaFormResponse(formDefinition), [formDefinition]);
  const formFields = useMemo(() => flattenFormSections(parsedForm.sections), [parsedForm.sections]);

  const resetFlow = () => {
    setRoutes([]);
    setRouteMeta(null);
    setSelectedVisa(null);
    setFormDefinition(null);
    setFormValues({});
    setServiceFee(null);
    setRequiredDocuments([]);
    setDocumentGroupName('');
    setShowSubmitConfirm(false);
    setError('');
    setSuccess('');
  };

  const handleSearchRoutes = async (e) => {
    e.preventDefault();
    if (!fromCountry || !toCountry) {
      setError('Please select both from and to countries');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setSelectedVisa(null);
    setFormDefinition(null);
    setFormValues({});
    setServiceFee(null);
    setRequiredDocuments([]);
    setDocumentGroupName('');

    try {
      const res = await axios.post(
        `${API_URL}/visa-apply/routes`,
        { from_country: fromCountry, to_country: toCountry },
        freshRequestConfig
      );
      setRoutes(res.data.routes || []);
      setRouteMeta({
        from_country_id: res.data.from_country_id,
        to_country_id: res.data.to_country_id,
      });
      if (!res.data.routes?.length) {
        setError('No visa types are available for this route');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load visa options');
      setRoutes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectVisa = async (visa) => {
    setLoading(true);
    setError('');
    setSuccess('');
    setSelectedVisa(visa);
    setRequiredDocuments([]);
    setDocumentGroupName('');

    const formParams = getVisaFormRequestParams(visa, routeMeta);
    const visaTypeName = visa.type_of_visa || visa.visa_type || visa.visatype;
    const routeVisaId = getRouteFormTemplateId(visa);

    try {
      const [formRes, docsRes] = await Promise.all([
        axios.post(`${API_URL}/visa-apply/form`, formParams, freshRequestConfig),
        axios
          .get(`${API_URL}/visa-apply/documents`, {
            ...freshRequestConfig,
            params: {
              from_country: routeMeta.from_country_id,
              to_country: routeMeta.to_country_id,
              visaid: routeVisaId,
              _t: Date.now(),
            },
          })
          .catch(() => null),
      ]);

      const parsed = parseVisaFormResponse(formRes.data);
      if (!parsed.sections.length) {
        setError(
          formRes.data?.message ||
            'No application form is available for this visa type yet. Please choose another option or contact support.'
        );
        setSelectedVisa(null);
        return;
      }

      setFormDefinition(formRes.data);
      setFormValues({});

      if (docsRes?.data) {
        setRequiredDocuments(docsRes.data.documents || []);
        setDocumentGroupName(docsRes.data.groups?.[0]?.name || '');
      }

      try {
        const feeRes = await axios.get(`${API_URL}/visa-apply/service-fee`, {
          ...freshRequestConfig,
          params: {
            from_country: routeMeta.from_country_id,
            to_country: routeMeta.to_country_id,
            visa_type: visaTypeName,
            _t: Date.now(),
          },
        });
        setServiceFee(feeRes.data.charge);
      } catch {
        setServiceFee(null);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load visa application form');
      setSelectedVisa(null);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitApplication = (e) => {
    e.preventDefault();
    if (!selectedVisa || !routeMeta) return;

    const missing = formFields.filter((field) => field.required && !formValues[field.name]);
    if (missing.length > 0) {
      setError(`Please fill required fields: ${missing.map((f) => f.label).join(', ')}`);
      return;
    }

    setError('');
    setShowSubmitConfirm(true);
  };

  const closeSubmitConfirm = () => {
    if (submitting) return;
    setShowSubmitConfirm(false);
  };

  const confirmSubmitApplication = async () => {
    if (!selectedVisa || !routeMeta) return;

    setSubmitting(true);
    setError('');
    setSuccess('');

    const formParams = getVisaFormRequestParams(selectedVisa, routeMeta);
    const visaTypeName =
      selectedVisa.type_of_visa || selectedVisa.visa_type || selectedVisa.visatype;

    try {
      await axios.post(`${API_URL}/visa-apply/submit`, {
        from_country: routeMeta.from_country_id,
        to_country: routeMeta.to_country_id,
        visa_type: visaTypeName,
        gid: formParams.gid,
        visaid: formParams.visaid,
        visatype: formParams.visatype,
        form_params: formParams,
        customer_visa_application_data: {
          ...formValues,
          visa_name: parsedForm.meta?.visaName || visaTypeName,
          form_sections: parsedForm.sections.map((s) => s.title),
          form_field_labels: buildFormFieldLabels(parsedForm.sections),
          form_params: formParams,
          form_filled: true,
        },
      });

      setShowSubmitConfirm(false);
      setSuccess('Visa application submitted successfully. It will be processed by the corporate team.');
      if (onSubmitted) onSubmitted();
      setSelectedVisa(null);
      setFormDefinition(null);
      setFormValues({});
      setRequiredDocuments([]);
      setDocumentGroupName('');
      setRoutes([]);
    } catch (err) {
      setShowSubmitConfirm(false);
      setError(err.response?.data?.error || 'Failed to submit visa application');
    } finally {
      setSubmitting(false);
    }
  };

  const visaDisplayName =
    parsedForm.meta?.visaName ||
    selectedVisa?.type_of_visa ||
    selectedVisa?.visa_type ||
    'this visa';

  return (
    <div className="space-y-6">
      {showSubmitConfirm && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={closeSubmitConfirm}
        >
          <div
            className="bg-white rounded-2xl p-6 w-full max-w-lg mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirm Submission</h3>
            <p className="text-sm text-gray-600 mb-4">
              Submit the application for <strong>{visaDisplayName}</strong>? The service fee will be
              deducted from the company vault and the application will be sent for processing.
            </p>
            {serviceFee && (
              <div className="mb-4">
                <FeeSummary serviceFee={serviceFee} />
              </div>
            )}
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={closeSubmitConfirm}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmSubmitApplication}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium bg-primary hover:bg-primary-hover text-white rounded-xl transition-colors disabled:opacity-50"
              >
                {submitting ? 'Submitting...' : 'Confirm & Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div>
        <h2 className="primary-title">Apply Visa</h2>
        <p className="text-ink-secondary text-sm mt-1">
          Select your travel route, choose a visa type, and complete the application form.
        </p>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>
      )}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm">{success}</div>
      )}

      <form onSubmit={handleSearchRoutes} className="bg-white rounded-crm border border-border p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <CountrySelect
            label="From Country"
            value={fromCountry}
            onChange={(next) => {
              setFromCountry(next);
              resetFlow();
            }}
            countries={countries}
            loading={countriesLoading}
            placeholder="Select origin country"
            emptyLabel="Select origin country"
            required
          />
          <CountrySelect
            label="To Country"
            value={toCountry}
            onChange={(next) => {
              setToCountry(next);
              resetFlow();
            }}
            countries={countries}
            loading={countriesLoading}
            placeholder="Select destination country"
            emptyLabel="Select destination country"
            required
          />
        </div>
        <div className="mt-4 flex gap-3">
          <button
            type="submit"
            disabled={loading}
            className="btn-primary"
          >
            {loading && !selectedVisa ? 'Searching...' : 'Search Available Visas'}
          </button>
        </div>
      </form>

      {routes.length > 0 && !selectedVisa && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {routes.map((visa, index) => {
            const key = [
              visa.visa_type_id,
              visa.id,
              visa.process,
              visa.process_docu,
              index,
            ]
              .filter((part) => part != null && part !== '')
              .join('-');
            const processLabel = visa.process || visa.process_name;
            return (
              <div key={key} className="bg-white border border-gray-100 rounded-2xl p-5">
                <h3 className="text-lg font-semibold text-gray-900">
                  {visa.type_of_visa || visa.visa_type || 'Visa'}
                  {processLabel ? ` — ${processLabel}` : ''}
                </h3>
                <p className="text-sm text-gray-500 mt-1">{visa.description || 'Visa application'}</p>
                <div className="mt-3 space-y-1 text-sm text-gray-600">
                  {visa.visa_validity && <p>Validity: {visa.visa_validity}</p>}
                  {visa.length_of_stay && <p>Length of stay: {visa.length_of_stay}</p>}
                  {visa.entry_type && <p>Entry: {visa.entry_type}</p>}
                  {visa.time_to_get_visa && <p>Processing time: {visa.time_to_get_visa}</p>}
                  {visa.price && <p className="font-medium text-gray-900">Visa price: {visa.price}</p>}
                  {visa.service_charge && <p>Service charge: {visa.service_charge}</p>}
                </div>
                <button
                  type="button"
                  onClick={() => handleSelectVisa(visa)}
                  disabled={loading || visa.can_apply === false}
                  className="mt-4 bg-primary text-white px-4 py-2 rounded-crm text-sm font-medium hover:bg-primary-hover disabled:opacity-50"
                >
                  {visa.can_apply === false ? 'Not available' : 'Apply'}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {selectedVisa && (
        <form onSubmit={handleSubmitApplication} className="bg-white rounded-crm border border-border p-6 space-y-6">
          {loading && !formDefinition ? (
            <p className="text-sm text-gray-500 py-8 text-center">Loading visa application form...</p>
          ) : (
            <>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                {parsedForm.meta?.visaName || selectedVisa.type_of_visa}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Complete the application form below
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setSelectedVisa(null);
                setFormDefinition(null);
                setFormValues({});
                setServiceFee(null);
                setRequiredDocuments([]);
                setDocumentGroupName('');
              }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Back
            </button>
          </div>

          {serviceFee && <FeeSummary serviceFee={serviceFee} />}

          {requiredDocuments.length > 0 && (
            <div className="bg-primary-soft border border-primary/10 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-ink mb-2">
                Required Documents
                {documentGroupName ? ` — ${documentGroupName}` : ''}
              </h4>
              <ul className="space-y-1.5 text-sm text-ink-table list-disc pl-5">
                {requiredDocuments.map((doc) => (
                  <li key={doc.id}>{doc.name}</li>
                ))}
              </ul>
            </div>
          )}

          {parsedForm.meta?.notes?.length > 0 && (
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 text-sm text-amber-900 space-y-2">
              {parsedForm.meta.notes.map((note, index) => (
                <p key={index}>{note}</p>
              ))}
            </div>
          )}

          {parsedForm.sections.map((section) => (
            <div key={section.title} className="space-y-4">
              <h4 className="text-lg font-semibold text-gray-900 border-b border-gray-100 pb-2">
                {section.title}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {section.fields.map((field) => (
                  <div key={field.name}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {field.label}
                      {field.required ? ' *' : ''}
                    </label>
                    <VisaFormField
                      field={field}
                      value={formValues[field.name]}
                      onChange={handleFieldChange}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}

          <button
            type="submit"
            disabled={submitting || formFields.length === 0}
            className="btn-primary"
          >
            {submitting ? 'Submitting...' : 'Submit Visa Application'}
          </button>
            </>
          )}
        </form>
      )}
    </div>
  );
};

export default ApplyVisaSection;
